import type { ServerToClientEvent } from '@atlassian/jira-s2c-event/src/index.tsx';
import { getClientDispatcher } from '@atlassian/jira-s2c-client-dispatcher/src/index.tsx';

const ELEMENT_SELECTOR = 'script[data-clientjs]';

let receiver: ClientReceiver;

type ServerToClientEventHandler = (payload: ServerToClientEvent) => void;

class ClientReceiver {
	#doc: Document;

	#fulfilled: Promise<void>;

	#onEvent: ServerToClientEventHandler;

	constructor(doc: Document, onEvent: ServerToClientEventHandler) {
		this.#doc = doc;
		this.#onEvent = onEvent;

		// consume any instructions that arrived before we initialised
		this.#doc
			.querySelectorAll(ELEMENT_SELECTOR)
			.forEach((el) => this.#handleServerToClientElement(el));

		// Bail early if DOM is already ready, otherwise observe for new instructions until it is.
		this.#fulfilled =
			this.#doc.readyState === 'complete'
				? Promise.resolve()
				: new Promise((resolve) => {
						this.#observe(resolve);
					});
	}

	#observe(resolve: () => void) {
		// observe the document for changes
		const observer = new MutationObserver((mutations) => this.#handleMutation(mutations));
		observer.observe(this.#doc.body, { attributes: false, childList: true, subtree: false });

		// only keep trying until DOM ready, then give up.
		this.#doc.addEventListener(
			'DOMContentLoaded',
			() => {
				setTimeout(() => {
					observer.disconnect();
					resolve();
				}, 0);
			},
			{ once: true },
		);
	}

	#handleMutation(mutations: MutationRecord[]) {
		for (const mutation of mutations) {
			if (mutation.type === 'childList') {
				for (const node of mutation.addedNodes) {
					if (node.nodeType === Node.ELEMENT_NODE && node instanceof HTMLElement) {
						if (node.matches(ELEMENT_SELECTOR)) {
							setTimeout(() => this.#handleServerToClientElement(node), 4);
						}
					}
				}
			}
		}
	}

	#handleServerToClientElement(el: Element) {
		const events = el.textContent?.trim().split('\n');
		if (!events) {
			return;
		}
		for (const event of events) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const payload = JSON.parse(event) as ServerToClientEvent;
			this.#onEvent(payload);
		}
	}

	fulfilled() {
		return this.#fulfilled;
	}
}

function getClientReceiver() {
	// special-case: if server, bail early and do nothing
	if (__SERVER__ || !globalThis.document) {
		return;
	}
	if (!receiver) {
		const dispatcher = getClientDispatcher();
		receiver = new ClientReceiver(globalThis.document, dispatcher.ingest.bind(dispatcher));
	}
	return receiver;
}

export { getClientReceiver, type ClientReceiver };
